/**
 * Frontend `fetch` wrapper.
 *
 * Replaces the ~25 inline `fetch().then(r => r.json())` chains scattered
 * across painel + dashboard + admin pages. Goals:
 *
 *   - **Single source of timeout + abort behaviour.** Every external
 *     request gets a default 15s deadline, can be overridden, and
 *     composes with the caller's `signal` so React effect cleanups
 *     still cancel in-flight loads.
 *   - **Typed errors.** A 4xx/5xx surfaces as `ApiError` carrying the
 *     status + the JSON `error` field. Callers can `catch (err) { if
 *     (err instanceof ApiError && err.status === 401) … }` without
 *     reinventing the parsing dance every time.
 *   - **Cookie-based auth by default.** Every consumer of our own
 *     `/api/*` namespace needs `credentials: "include"`; making it the
 *     default removes a footgun where someone forgets it and silently
 *     gets a 401.
 *
 * Not a replacement for SWR / React Query — pages still drive their own
 * lifecycle. This is just the "make one request" surface.
 */

/** Default deadline. Mirrors the server-side fetchWithTimeout choice
 *  for /api/auth/me + /api/billing/me which are P95 < 500ms. */
const DEFAULT_TIMEOUT_MS = 15_000;

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  readonly endpoint: string;
  constructor(
    endpoint: string,
    status: number,
    body: unknown,
    message?: string
  ) {
    super(message ?? `api_${status}: ${endpoint}`);
    this.name = "ApiError";
    this.endpoint = endpoint;
    this.status = status;
    this.body = body;
  }
}

export class ApiTimeoutError extends Error {
  readonly endpoint: string;
  readonly timeoutMs: number;
  constructor(endpoint: string, timeoutMs: number) {
    super(`api_timeout after ${timeoutMs}ms: ${endpoint}`);
    this.name = "ApiTimeoutError";
    this.endpoint = endpoint;
    this.timeoutMs = timeoutMs;
  }
}

export interface ApiInit extends Omit<RequestInit, "body" | "headers"> {
  /** Deadline override. Defaults to 15s — set higher for known-slow calls. */
  timeoutMs?: number;
  /** JSON body. Auto-stringified + sets Content-Type. */
  json?: unknown;
  /** FormData body. Skips JSON serialization. */
  form?: FormData;
  /** Extra headers merged on top of defaults. */
  headers?: HeadersInit;
}

/**
 * Make a request to one of our own `/api/*` endpoints and parse the
 * response as JSON. Throws `ApiError` for non-2xx, `ApiTimeoutError`
 * when the deadline hits, and propagates the caller's `AbortError`
 * unchanged.
 */
export async function fetchJson<T = unknown>(
  url: string,
  init: ApiInit = {}
): Promise<T> {
  const {
    timeoutMs = DEFAULT_TIMEOUT_MS,
    json,
    form,
    headers,
    signal: callerSignal,
    credentials,
    ...rest
  } = init;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort(callerSignal.reason);
    else
      callerSignal.addEventListener(
        "abort",
        () => controller.abort(callerSignal.reason),
        { once: true }
      );
  }

  const mergedHeaders = new Headers(headers);
  let body: BodyInit | undefined;
  if (json !== undefined) {
    if (!mergedHeaders.has("Content-Type")) {
      mergedHeaders.set("Content-Type", "application/json");
    }
    body = JSON.stringify(json);
  } else if (form !== undefined) {
    // Leaving Content-Type off — fetch sets the multipart boundary itself.
    body = form;
  }

  let res: Response;
  try {
    res = await fetch(url, {
      ...rest,
      headers: mergedHeaders,
      body,
      credentials: credentials ?? "include",
      signal: controller.signal,
    });
  } catch (err) {
    if (
      err instanceof DOMException &&
      err.name === "AbortError" &&
      !callerSignal?.aborted
    ) {
      throw new ApiTimeoutError(url, timeoutMs);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }

  // Some endpoints (e.g. 204 logout) intentionally return no body — let
  // those resolve as `null` rather than failing the parse.
  const text = await res.text();
  const parsed: unknown = text ? safeJson(text) : null;

  if (!res.ok) {
    throw new ApiError(url, res.status, parsed);
  }
  return parsed as T;
}

/**
 * For endpoints that only need success-or-failure semantics (POST that
 * returns 204, or routes whose body is irrelevant). Throws on non-2xx.
 */
export async function fetchOk(
  url: string,
  init: ApiInit = {}
): Promise<void> {
  await fetchJson<unknown>(url, init);
}

/**
 * Permissive JSON parse — falls back to the raw string so a server that
 * returned `{}` on an error still surfaces useful detail via ApiError.body.
 */
function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
